import { useState } from 'react';
import _, { isEmpty } from 'lodash';

import { Actions, Async, F1, SMap } from '../../helpers/types';
import { CollectionsModel } from './collections.domain';
import { SalesType } from '@client/new/common/types';
import { splitCollections } from './collectionMappers';
import { Refetch, useCollections } from './hooks';
import { asyncAction } from '@client/new/helpers/general';
import {
  useOrderCollectionsMutation,
  useArchiveCollectionMutation,
  usePublishCollectionMutation,
  useDeleteCollectionMutation,
  BundlePricing,
  BundleStatus,
} from 'generated/graphql';
import { isAllFinished, makeAsync } from '@client/new/helpers/async';
import { trackEvent } from '@services/tracking';
import { BusinessModel, Influencer } from '@playbook-technologies/common-enums';
import { useInfluencer } from '@client/new/common/LiteUser.mapper';
import { useDuplicateCollection } from './collection.query';
import toast from 'react-hot-toast';
import { toPublishBundleIds } from '../collection/mappers';

export type UseCollectionsModelParams = {
  type: SalesType;
  influencerId?: string;
  salesType?: SalesType;
  locationSearch?: string;
};

type CollectionsAction = Actions<{
  deleteProgram: number;
  archiveProgram: number;
  publishProgram: number;
  reorder: Array<number>;
  duplicateProgram: number;
  setPublishToPlatformBundle: boolean;
}>;

export type CollectionsDispatcher = F1<CollectionsAction, null>;

type State = {
  results: SMap<Async>;
  collectionOrder: Array<number>;
};

export const useCollectionsModel = (
  p: UseCollectionsModelParams,
): Async<CollectionsModel & { dispatch: CollectionsDispatcher }> => {
  if (!p.salesType) p.salesType = p.type;
  const asyncInfluencer = useInfluencer(
    p.influencerId ? Number(p.influencerId) : null,
  );
  const [programStateMap, setProgramStateMap] = useState<
    Pick<CollectionsModel, 'programStateMap'>
  >({ programStateMap: {} });
  const [state, setState] = useState<State>({
    results: {},
    collectionOrder: [],
  });
  const [publishToPlatformBundle, setPublishToPlatformBundle] = useState(true);

  const [orderCollectionMutation] = useOrderCollectionsMutation();
  const [archiveCollectionMutation] = useArchiveCollectionMutation();
  const [publishCollectionMutation] = usePublishCollectionMutation();
  const [deleteCollectionMutation] = useDeleteCollectionMutation();
  const { mutateAsync: duplicateCollection } = useDuplicateCollection();

  const influencerId =
    asyncInfluencer.type === 'fetched' ? asyncInfluencer.value.id : null;

  const res = useCollections(influencerId || null, p.type);

  if (asyncInfluencer.type === 'fetching') {
    return {
      type: 'fetching',
    };
  }

  if (asyncInfluencer.type === 'fetched' && !asyncInfluencer.value.id) {
    return {
      type: 'fetch-error',
      value: 'Creator id is missing',
    };
  }

  if (res.type !== 'fetched') {
    return res;
  }

  const reorderCollection = (payload: number[], refetch: Refetch) => {
    const id = new Date().toLocaleDateString();
    setState((prev) => ({
      ...prev,
      collectionOrder: payload,
      results: { ...prev.results, [id]: makeAsync.fetching() },
    }));

    influencerId &&
      orderCollectionMutation({
        variables: {
          influencerId: influencerId,
          ids: payload,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          salesType: p.salesType as any,
        },
      })
        .then(() =>
          setState((prev) => ({
            ...prev,
            results: { ...prev.results, [id]: makeAsync.fetched({}) },
          })),
        )
        .catch((err) => {
          setState((prev) => ({
            ...prev,
            results: { ...prev.results, [id]: makeAsync.error(err) },
          }));
          refetch();
        });
    return null;
  };

  const setFetchingState = (id: number | string, value: Async) => {
    setProgramStateMap((vs) => ({
      programStateMap: {
        ...vs.programStateMap,
        [id]: value,
      },
    }));
  };

  const dispatch: CollectionsDispatcher = (action) => {
    switch (action.type) {
      case 'archiveProgram':
        return asyncAction(
          (v) => setFetchingState(action.payload, v),
          () =>
            archiveCollectionMutation({
              variables: { collectionId: action.payload },
            })
              .then(() => res.value.refetch())
              .catch((err) => {
                toast.error(err.message);
              }),
        );
      case 'deleteProgram':
        return asyncAction(
          (v) => setFetchingState(action.payload, v),
          () =>
            deleteCollectionMutation({
              variables: { collectionId: action.payload },
            }).then(() => {
              trackEvent({
                type: 'Collection Deleted',
                payload: {
                  'Collection Type': 'Program',
                },
              });
              res.value.refetch();
            }),
        );
      case 'reorder':
        return reorderCollection(action.payload, res.value.refetch);
      case 'publishProgram':
        return asyncAction(
          (v) => setFetchingState(action.payload, v),
          () => {
            const bundleIds =
              res.value.businessModel === BusinessModel.Legacy ||
              p.type === 'otp'
                ? null
                : toPublishBundleIds(
                    publishToPlatformBundle,
                    res.value.bundles,
                  );

            return publishCollectionMutation({
              variables: {
                programId: action.payload,
                onlyPublish: true,
                bundleIds,
              },
            })
              .then(() => res.value.refetch())
              .catch((err) => {
                toast.error(err.message);
              });
          },
        );
      case 'duplicateProgram':
        return asyncAction(
          (v) => setFetchingState(action.type, v),
          () =>
            duplicateCollection({ collectionId: action.payload }).then((c) => {
              res.value.refetch();
              return c;
            }),
        );
      case 'setPublishToPlatformBundle':
        setPublishToPlatformBundle(action.payload);
        return null;
    }
  };

  const orderedLiteCollections = !_.isEmpty(state.collectionOrder)
    ? res.value.liteCollections.map((i) => ({
        ...i,
        index: state.collectionOrder.indexOf(i.programId),
      }))
    : res.value.liteCollections;
  const collections = splitCollections(orderedLiteCollections);

  const influencer =
    asyncInfluencer.type === 'fetched' ? asyncInfluencer.value : undefined;

  const isCreatorLive =
    asyncInfluencer.type === 'fetched' &&
    asyncInfluencer.value.mode === Influencer.Mode.Live.value;

  const hasPlatformBundle = !isEmpty(
    res.value.bundles.filter((b) => b.isPlatformBundle),
  );

  const subscriptionBundles = res.value.bundles.filter(
    (b) =>
      b.pricing === BundlePricing.Subscription &&
      b.status === BundleStatus.Published,
  );

  const isPlatformBundleCreator =
    !isEmpty(subscriptionBundles) &&
    subscriptionBundles.every((b) => b.isPlatformBundle);

  return {
    type: 'fetched',
    value: {
      type: p.type,
      ...programStateMap,
      collections,
      dispatch,
      influencerId: influencerId || 0,
      isReordering: !isAllFinished(state.results),
      hasAnyCollection: !isEmpty(orderedLiteCollections),
      influencerName: influencer?.firstName || '',
      customURL: influencer?.customURL,
      isCreatorLive,
      publishToPlatformBundle,
      isBundleCreator: res.value.businessModel === BusinessModel.Bundle,
      isOtpMigratedToBundle: res.value.isOtpMigratedToBundle,
      isOffers35Enabled: influencer?.customizations?.isOffers35Enabled ?? false,
      hasPlatformBundle,
      isPlatformBundleCreator,
      hasAnyBundle: !isEmpty(res.value.bundles),
      bundles: res.value.bundles,
      featureCodeById: res.value.featureCodeById,
    },
  };
};
